import { kc } from './keycloak.js';

export interface CurrentUser {
  /** Keycloak `sub`. Sent to the API for analystId/approverId writes
   *  (resolvePrincipals translates it); never compare against case columns. */
  id: string;
  /** Server-generated users.id (from GET /api/v1/me). This is the ID space
   *  cases.analystId / approverId live in — use for principal comparisons.
   *  Null until the bootstrap /me fetch resolves (or if it failed). */
  serverId: string | null;
  name: string;
  email: string;
  roles: string[];
  canEdit: boolean;
  canApprove: boolean;
  canAdmin: boolean;
  isLegal: boolean;
}

let serverUserId: string | null = null;

// Called once from main.tsx after the /me fetch; tests may reset it.
export function setServerUserId(id: string | null): void {
  serverUserId = id;
}

// Dual-control guard: is this user the case's assigned analyst? Compares in
// the server ID space only. Returns false while serverId is unresolved — the
// client guard is UX; the dual_control_distinct_principals CHECK plus the
// approverId written on decisions/final determinations is the enforcement.
export function isAssignedAnalyst(
  analystId: string | null | undefined,
  user: CurrentUser | null,
): boolean {
  return !!user?.serverId && analystId === user.serverId;
}

interface ParsedToken {
  sub?: string;
  name?: string;
  email?: string;
  realm_access?: { roles?: string[] };
}

export function getCurrentUser(): CurrentUser | null {
  const t = kc.tokenParsed as ParsedToken | undefined;
  if (!t?.sub) return null;
  const roles = t.realm_access?.roles ?? [];
  const has = (r: string) => roles.includes(r);
  return {
    id: t.sub,
    serverId: serverUserId,
    name: t.name ?? '',
    email: t.email ?? '',
    roles,
    canEdit: has('bcid-analyst') || has('bcid-admin'),
    canApprove: has('bcid-approver') || has('bcid-admin'),
    canAdmin: has('bcid-admin'),
    isLegal: has('bcid-legal'),
  };
}
